import { _ } from "~/i18n";
import { IMapyBoxItem, IMapyboxItemUpdateEvent, IMapyboxTrackLinkEvent, ISDSIconMulti } from "~/interfaces";
import { node, icon } from "~/util/dom";
import { createPremiumIconWithTooltip, openPropositionDialog } from "~/util/premium";
import { createUserNote } from "../user-note/note";
import MyMapsEditNote from "../dialogs/mymaps-edit-note";
import { getFolders, ITrackListFolderItem, trackListFolders } from "~/util/providers/mapybox";
import Dialog from "../dialog";
import MyMapsContextMenu, { TMymapsContextMenuName } from "~/es6/mymaps.contextmenu";
import BoxTracksContextMenu from "./box-tracks-contextmenu";
import MyMapsMoveItems from "../dialogs/mymaps-move-items";
import MyMapsRemove from "../dialogs/mymaps-remove";
import createSDSIcon from "../icon";
import { MYMAPS_DROP_DOWN, MYMAPS_DROP_UP, MYMAPS_FOLDER_STAR, MYMAPS_STAR, MYMAPS_VISITED_EMPTY, MYMAPS_VISITED_FILLED, MYMAPS_ETC } from "../sds-compatible-icons";
import { getMapyboxItemAnalyticsType, hasVisitedOption, mapyboxAnalyticsEdit, mymapsOpenFolder, updateMapyBoxItem } from "~/util/mapybox";
import { store } from "~/state/store";
import { MAPYBOX_ITEM_UPDATE_EVENT_NAME, MAPYBOX_TRACK_LINK_EVENT_NAME } from "./const";

export type TMymapsBoxSignal = {
	name: "delete";
} | {
	name: "track-link-delete";
} | {
	name: "like-update";
	updateData: Partial<IMapyBoxItem>;
} | {
	name: "note-open";
	container: HTMLDivElement;
} | {
	name: "like-move";
	folderId: string; folderTitle: string;
};

interface IFolder {
	id: string;
	title: string;
}

interface IOpts {
	data?: IMapyBoxItem;
	folder?: IFolder;
	isPremiumUser?: boolean;
	onSignal?: (signal: TMymapsBoxSignal) => void;
}

interface ICreateTopItem {
	iconName: string;
	sdsIcon?: ISDSIconMulti;
	text: string;
	addPremium?: boolean;
	onClick: () => void;
}

const HAS_NOTE_CLASS = "has-note";
const HAS_TOP_SECTION_CLASS = "has-top";
const OPEN_CLASS = "open";

function createTopItem({
	iconName,
	sdsIcon,
	text,
	addPremium = false,
	onClick = () => {},
}: ICreateTopItem) {
	const topItem = node("div", { className: "mymapsBox__topItem" });
	const visitedItemBtn = node("button", { className: "mymapsBox__topItemBtn", type: "button" });
	const visitedItemText = node("span", { className: "mymapsBox__topItemText", textContent: text });
	let currentIcon = sdsIcon
		? createSDSIcon({ className: "sds-icon", symbol: sdsIcon })
		: icon(iconName);

	visitedItemBtn.append(
		currentIcon,
		visitedItemText,
	);
	visitedItemBtn.addEventListener("click", () => {
		if (addPremium) {
			openPropositionDialog();
		} else {
			onClick();
		}
	});
	topItem.append(visitedItemBtn);

	if (addPremium) {
		topItem.append(createPremiumIconWithTooltip());
	}

	return {
		container: topItem,
		text: visitedItemText,
		changeMapyIcon: (newSdsIcon: ISDSIconMulti) => {
			const newIconElem = createSDSIcon({ className: "sds-icon", symbol: newSdsIcon });

			currentIcon.before(newIconElem);
			currentIcon.remove();
			currentIcon = newIconElem;
		},
	};
}

function removeDialog(data: IMapyBoxItem, isRemove = true) {
	return new Promise(resolve => {
		const dialog = new MyMapsRemove({
			data,
			operation: isRemove
				? "remove"
				: "delete",
			onRemove: () => {
				resolve({});
			},
		});

		dialog.open();
	});
}

function getFolderLink(folderTitle: string, folderId: string) {
	const folderLink = node("a", { textContent: folderTitle, href: "#" });

	folderLink.addEventListener("click", event => {
		event.stopPropagation();
		event.preventDefault();
		// prechod do slozky
		mymapsOpenFolder(folderId);
	});

	return folderLink;
}

export default class MyMapsBox {
	_opts: IOpts;

	_dom: {
		container: HTMLDivElement;
		top: HTMLDivElement;
		bottom: HTMLDivElement;
		savedItem: HTMLDivElement;
		visitedItem: HTMLDivElement;
	};

	_editNote: MyMapsEditNote;

	_userNote: ReturnType<typeof createUserNote>;

	_addNoteBtn: HTMLDivElement;

	_contextMenu: MyMapsContextMenu;

	_tracksContextMenu: BoxTracksContextMenu;

	_optsBtn: HTMLButtonElement;

	_rootId: string;

	_trackFolders: Array<ITrackListFolderItem>;

	constructor(opts: IOpts) {
		this._opts = {
			isPremiumUser: false,
			onSignal: () => {},
			...opts,
		};
		this._editNote = null;
		this._userNote = null;
		this._addNoteBtn = null;
		this._contextMenu = null;
		this._tracksContextMenu = null;
		this._optsBtn = null;
		this._rootId = store.getState().mymaps.rootId;
		this._trackFolders = [];

		this._build();
		this._buildSavedItem();
		this._buildVisitedItem();
		this._buildNote();
		this._emptyCheck();

		// nacteme si seznam slozek do kese
		getFolders();

		// pro tracky nacteme linky
		this.refreshTrackList();

		// @ts-ignore
		window.addEventListener(MAPYBOX_ITEM_UPDATE_EVENT_NAME, this);
		window.addEventListener(MAPYBOX_TRACK_LINK_EVENT_NAME, this);
	}

	get container() {
		return this._dom.container;
	}

	destroy() {
		this._dom.container.remove();
		this._editNote?.destroy();
		this._userNote?.destroy();
		window.removeEventListener(MAPYBOX_ITEM_UPDATE_EVENT_NAME, this);
		window.removeEventListener(MAPYBOX_TRACK_LINK_EVENT_NAME, this);
	}

	print() {
		this._userNote?.print();
	}

	refreshTrackList() {
		if (this._opts.data?.type === "track") {
			this._loadTrackList();
		}
	}

	rebuild(updData?: Partial<IMapyBoxItem>) {
		if (updData) {
			Object.assign(this._opts.data, updData);
		}

		this._tracksContextMenu = null;
		this._contextMenu = null;

		this._dom.savedItem?.remove();
		this._buildSavedItem();
		this._emptyCheck();
	}

	handleEvent(event: any) {
		if (event.type === MAPYBOX_ITEM_UPDATE_EVENT_NAME) {
			const data: IMapyboxItemUpdateEvent = event.detail;

			if (data.source === "edit-props-dialog" && data.id === this._opts.data?.id) {
				// aktualizace dat
				Object.assign(this._opts.data, data);
				// aktualizace poznamky
				this._updateNoteData(data.data.description, true);
				// rebuild navstiveno
				this._buildVisitedItem();
				// signal
				this._opts.onSignal({
					name: "like-update",
					updateData: data.data,
				});
			}
		} else if (event.type === MAPYBOX_TRACK_LINK_EVENT_NAME) {
			const data: IMapyboxTrackLinkEvent = event.detail;

			if (data.id === this._opts.data?.id) {
				this._loadTrackList();
			}
		}
	}

	_build() {
		const container = node("div", { className: "mymapsBox" });
		const top = node("div", { className: "mymapsBox__top" });
		const bottom = node("div", { className: "mymapsBox__bottom" });

		container.append(top, bottom);

		this._dom = {
			container,
			top,
			bottom,
			savedItem: null,
			visitedItem: null,
		};
	}

	_buildSavedItem() {
		if (this._opts.folder || this._trackFolders.length > 0) {
			const savedItem = node("div", { className: "mymapsBox__savedItem" });
			const textCont = node("div", { className: "mymapsBox__savedItemText" });
			const isTrack = this._opts.data?.type === "track" || this._opts.data?.linktype === "track";
			const multipleTrackLinks = this._trackFolders.length > 1;

			savedItem.append(
				isTrack
					? createSDSIcon({ className: "sds-icon sds-icon-folder-star", symbol: MYMAPS_FOLDER_STAR })
					: createSDSIcon({ className: "sds-icon sds-icon-star", symbol: MYMAPS_STAR }),
				textCont,
			);

			const homeOrWork = this._opts.data?.liketype && this._opts.data.liketype !== "like" && this._opts.data.liketype !== "link";

			// domu/prace
			if (homeOrWork) {
				textCont.textContent = this._opts.data.liketype === "home"
					? _("detail.content.mymapsplace.savedHome")
					: _("detail.content.mymapsplace.savedWork");
			// track - ulozeny ve vice slozkach
			} else if (isTrack) {
				textCont.textContent = `${_("detail.content.mymapsplace.linkTo")} `;

				if (this._trackFolders.length === 1) {
					const curFolder = this._trackFolders[0];
					const isRootFolder = curFolder.id === this._rootId;
					const folderTitle = isRootFolder
						? _("detail.content.mymapsplace.saved2")
						: curFolder.title;
					const curFolderId = this._opts.data?.parent_id || "";

					textCont.append(getFolderLink(folderTitle, curFolderId));
				}
			} else {
				const isRootFolder = this._opts.data?.parent_id === this._rootId;
				const folderTitle = this._opts.folder?.title || "";
				const curFolderId = this._opts.folder?.id || "";

				if (isRootFolder) {
					textCont.append(getFolderLink(_("detail.content.mymapsplace.saved2"), curFolderId));
				} else {
					textCont.textContent = `${_("detail.content.mymapsplace.savedInFolder")} `;
					textCont.append(getFolderLink(folderTitle, curFolderId));
				}
			}

			if (multipleTrackLinks) {
				const linksBtn = node("button", { className: `mymapsBox__linksBtn`, textContent: `${this._trackFolders.length} ${_("detail.content.mymapsplace.folders", { count: this._trackFolders.length })}` });

				linksBtn.append(createSDSIcon({ className: "icon icon-dropdown", symbol: MYMAPS_DROP_DOWN }));
				linksBtn.append(createSDSIcon({ className: "icon icon-dropup", symbol: MYMAPS_DROP_UP }));

				this._optsBtn = linksBtn;
				this._tracksContextMenu = new BoxTracksContextMenu({
					items: this._trackFolders,
					clickCb: (data, type) => {
						switch (type) {
							case "folder":
								mymapsOpenFolder(data.id);
								break;

							case "move":
								this._moveItemDialog(data.track_link_id, data.id, true);
								break;

							case "remove":
								this._trackLinkRemoveDialog({
									...this._opts.data,
									linktype: "track",
									id: data.track_link_id,
								}, true);
								break;

							default:
						}
					},
					onClose: () => {
						this._contextMenuClose();
					},
				});
				textCont.classList.add("multiple-tracklist");
				textCont.append(linksBtn);
				textCont.append(this._tracksContextMenu.container);
			} else if (!isTrack && !homeOrWork) {
				const optsBtn = node("button", { className: `mymapsBox__optsBtn` });

				optsBtn.append(createSDSIcon({ symbol: MYMAPS_ETC, className: "icon" }));

				this._optsBtn = optsBtn;
				this._contextMenu = new MyMapsContextMenu({
					clickCb: name => {
						this._contextMenuClick(name);
					},
					place: this._opts.data.linktype === "track"
						? "box-activity"
						: "box-normal",
					onClose: () => {
						this._contextMenuClose();
					},
				});
				textCont.append(optsBtn);
				textCont.append(this._contextMenu.container);
			}

			if (this._optsBtn) {
				this._optsBtn.addEventListener("click", () => {
					if (this._optsBtn.classList.contains("open")) {
						this._contextMenuClose();
					} else {
						this._contextMenuOpen();
					}
				});
			}

			this._dom.top.prepend(savedItem);
			this._dom.savedItem = savedItem;
		}
	}

	_getVisitedData() {
		// pouze pro premium uzivatele!
		return this._opts.isPremiumUser && this._opts.data.visited
			? {
				text: _("stateSwitch.items2.visit"),
				icon: MYMAPS_VISITED_FILLED,
			}
			: {
				text: _("mymap.visited.title"),
				icon: MYMAPS_VISITED_EMPTY,
			};
	}

	_buildVisitedItem() {
		if (this._dom.visitedItem) {
			this._dom.visitedItem.remove();
			this._dom.visitedItem = null;
		}

		if (hasVisitedOption(this._opts.data)) {
			let clickLock = false;

			const visitedData = this._getVisitedData();
			const createVisitedData = createTopItem({
				iconName: "",
				sdsIcon: visitedData.icon,
				text: visitedData.text,
				addPremium: !this._opts.isPremiumUser,
				onClick: async () => {
					if (clickLock) {
						return;
					}

					const newVisited = !(this._opts.data.visited || false);

					clickLock = true;

					await updateMapyBoxItem(this._opts.data, {
						visited: newVisited,
					});

					// analytika
					mapyboxAnalyticsEdit({
						type: getMapyboxItemAnalyticsType(this._opts.data),
						visited: newVisited,
						origin: "detail_box",
						alwaysOnMap: this._opts.data.always_on_map,
					});

					if (newVisited) {
						this._opts.data.visited = true;
					} else if ("visited" in this._opts.data) {
						delete this._opts.data.visited;
					}

					// zmena textace
					const newData = this._getVisitedData();

					createVisitedData.text.textContent = newData.text;
					createVisitedData.changeMapyIcon(newData.icon);

					this._sendUpdateSignal();
					/* eslint-disable-next-line */
					clickLock = false;
				},
			});

			this._dom.top.append(createVisitedData.container);
			this._dom.visitedItem = createVisitedData.container;
		}
	}

	_buildNote() {
		this._dom.bottom.innerHTML = "";

		if (this._opts.data.liketype !== "home" && this._opts.data.liketype !== "work") {
			if (this._addNoteBtn) {
				this._addNoteBtn.remove();
				this._addNoteBtn = null;
			}

			const hasText = (this._opts.data?.description || "") !== "";

			// zobrazit poznamku
			if (this._opts.isPremiumUser && hasText) {
				// poznamka
				this._userNote = createUserNote({
					data: this._opts.data,
					isPremiumUser: this._opts.isPremiumUser,
					onEdit: () => {
						this._onNoteEdit();
					},
					onRemove: () => {
						this._onNoteRemoveDialog();
					},
					onNoteOpen: () => {
						this._opts.onSignal({
							name: "note-open",
							container: this._userNote.container,
						});
					},
				});

				this._dom.bottom.append(this._userNote.container);
			// pridat poznamku
			} else {
				this._addNoteBtn = createTopItem({
					iconName: "edit-note",
					text: _("note.addNote"),
					addPremium: !this._opts.isPremiumUser,
					onClick: () => {
						this._onNoteEdit();
					},
				}).container;
				this._dom.top.append(this._addNoteBtn);
			}
		}
	}

	_createEditNoteDialog(): Promise<{
			note: string;
			request: boolean;
		}> {
		return new Promise(resolve => {
			const dialog = new MyMapsEditNote({
				data: this._opts.data,
				onSave: note => {
					this._editNote = null;
					resolve({
						note,
						request: true,
					});
				},
				onCancel: () => {
					this._editNote = null;
					resolve({
						note: "",
						request: false,
					});
				},
				onClose: () => {
					this._editNote = null;
					resolve({
						note: "",
						request: false,
					});
				},
			});

			dialog.open();
			this._editNote = dialog;
		});
	}

	_updateNoteData(description: string, withSignal = false) {
		this._opts.data.description = description;
		this._buildNote();
		this._emptyCheck();
		withSignal && this._opts.onSignal({
			name: "like-update",
			updateData: {
				description,
			},
		});
	}

	_sendUpdateSignal() {
		window.dispatchEvent(new CustomEvent(MAPYBOX_ITEM_UPDATE_EVENT_NAME, {
			detail: {
				id: this._opts.data.id,
				source: "box",
				data: {
					description: this._opts.data.description,
					...this._opts.data.visited
						? {
							visited: this._opts.data.visited,
						}
						: {},
				},
			} as IMapyboxItemUpdateEvent,
		}));
	}

	async _onNoteEdit() {
		const editData = await this._createEditNoteDialog();

		if (editData.request) {
			this._updateNoteData(editData.note, true);
			this._sendUpdateSignal();
			// analytika
			mapyboxAnalyticsEdit({
				type: getMapyboxItemAnalyticsType(this._opts.data),
				note: !!editData.note,
				origin: "detail_box",
				alwaysOnMap: this._opts.data.always_on_map,
			});
		}
	}

	async _onNoteRemove() {
		await updateMapyBoxItem(this._opts.data, {
			description: "",
		});
		// analytika
		mapyboxAnalyticsEdit({
			type: getMapyboxItemAnalyticsType(this._opts.data),
			note: false,
			origin: "detail_box",
			alwaysOnMap: this._opts.data.always_on_map,
		});
		this._updateNoteData("", true);
		this._sendUpdateSignal();
	}

	_onNoteRemoveDialog() {
		const dialog = new Dialog({
			title: _("note.removeNote"),
			className: "remove-note-dialog",
		});

		dialog.setBasic({
			closeTxt: "delete",
			bindEnter: true,
			cb: () => {
				this._onNoteRemove();
			},
		});
		dialog.open();
	}

	_emptyCheck() {
		const isTopEmpty = this._dom.top.children.length === 0;
		const isBottomEmpty = this._dom.bottom.children.length === 0;
		const isEmpty = isTopEmpty && isBottomEmpty;

		this._dom.container.classList[isEmpty ? "add" : "remove"]("is-empty");
		this._dom.container.classList[isTopEmpty ? "remove" : "add"](HAS_TOP_SECTION_CLASS);
		this._dom.container.classList[isBottomEmpty ? "remove" : "add"](HAS_NOTE_CLASS);
	}

	_contextMenuOpen() {
		this._optsBtn.classList.add(OPEN_CLASS);
		this._contextMenu?.open();
		this._tracksContextMenu?.open();
	}

	_contextMenuClose() {
		this._optsBtn.classList.remove(OPEN_CLASS);
		this._contextMenu?.close();
		this._tracksContextMenu?.close();
	}

	_moveItemDialog(id: string, curFolderId: string, isTrackLink?: boolean) {
		new MyMapsMoveItems({
			item: this._opts.data,
			ids: [id],
			moveFolders: isTrackLink
				? this._trackFolders.map(item => ({
					id: item.id,
					title: item.title,
				}))
				: [{
					id: curFolderId,
					title: this._opts.data.parent_name,
				}],
			onSave: (folderId, folderTitle) => {
				/* eslint-disable camelcase */
				if (this._opts.data) {
					this._opts.data.parent_id = folderId;
					this._opts.data.parent_name = folderTitle;
				}

				if (this._opts.folder) {
					this._opts.folder.id = folderId;
					this._opts.folder.title = folderTitle;
				}

				if (isTrackLink) {
					const filtered = this._trackFolders.filter(item => item.id === curFolderId);

					if (filtered.length === 1) {
						filtered[0].id = folderId;
						filtered[0].title = folderTitle;
					}
				}

				this.rebuild();
				this._opts.onSignal({
					name: "like-move",
					folderId,
					folderTitle,
				});
			},
		}).open();
	}

	async _trackLinkRemoveDialog(data: IMapyBoxItem, isRemove = true) {
		const count = this._trackFolders.length;

		await removeDialog(data, isRemove);

		this._opts.onSignal({
			name: "track-link-delete",
		});

		// byl pouze 1 zaznam -> odstranime se
		if (count === 1) {
			this.destroy();
			this._opts.onSignal({
				name: "delete",
			});
		} else {
			// updatujeme seznam
			this._trackFolders = this._trackFolders.filter(item => item.track_link_id !== data.id);
			// prekreslime
			this.rebuild();
		}
	}

	async _contextMenuClick(name: TMymapsContextMenuName) {
		switch (name) {
			case "activity-move-to-folder":
			case "move-to-folder": {
				this._moveItemDialog(this._opts.data.id, this._opts.data.parent_id, name === "activity-move-to-folder");
				break;
			}

			case "activity-remove":
			case "remove": {
				await removeDialog(this._opts.data, name === "activity-remove");

				this.destroy();
				this._opts.onSignal({
					name: "delete",
				});
				break;
			}

			default:
		}
	}

	async _loadTrackList() {
		const trackFolders = await trackListFolders(this._opts.data.id);
		const root = trackFolders.filter(item => item.type === "root")[0];

		if (root) {
			root.title = _("detail.content.mymapsplace.saved2");
		}

		this._trackFolders = trackFolders;

		// musime rebuildovat saved item
		this.rebuild();
	}
}
